import { ChangeDetectionStrategy, Component, LOCALE_ID, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { AnalyticsService } from '../../services/analytics.service';

type RecommendationId = 'sap_demo' | 'e_invoice_solution' | 'e_invoice_requirements' | 'incoming_invoice_guide';
type Placement = 'right_sidebar' | 'bottom_panel';

interface Recommendation {
  id: RecommendationId;
  domain: string;
  headline: string;
  description: string;
  url: string;
  href: string;
}

// Keep in sync with the .main-app-wrapper breakpoint in app.component.scss.
export const RECOMMENDATIONS_SIDEBAR_QUERY = '(min-width: 1200px)';
// localStorage key read by the portal (same origin) to choose the sign-up language.
const PORTAL_LOCALE_KEY = 'locale';

@Component({
  selector: 'app-recommendations',
  imports: [MatIconModule],
  templateUrl: './recommendations.component.html',
  styleUrl: './recommendations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecommendationsComponent {
  private readonly analytics = inject(AnalyticsService);
  private readonly locale = inject(LOCALE_ID);

  readonly isSidebar = toSignal(
    inject(BreakpointObserver).observe(RECOMMENDATIONS_SIDEBAR_QUERY).pipe(map(state => state.matches)),
    { initialValue: window.matchMedia(RECOMMENDATIONS_SIDEBAR_QUERY).matches }
  );
  readonly expanded = signal(false);

  readonly entries: Recommendation[] = ([
    {
      id: 'sap_demo',
      domain: 'business-one.cloud',
      headline: $localize`:@@einvoice.recommendations.sap_demo.headline:Try SAP Business One with e-invoicing`,
      description: $localize`:@@einvoice.recommendations.sap_demo.description:ERP and e-invoicing in one solution. Set up your free demo account.`,
      url: $localize`:@@einvoice.recommendations.sap_demo.url:https://www.business-one.cloud/signup`,
    },
    {
      id: 'e_invoice_solution',
      domain: 'business-one-consulting.com',
      headline: $localize`:@@einvoice.recommendations.e_invoice_solution.headline:E-invoicing with SAP Business One`,
      description: $localize`:@@einvoice.recommendations.e_invoice_solution.description:Discover an integrated solution for receiving, processing and sending e-invoices.`,
      url: $localize`:@@einvoice.recommendations.e_invoice_solution.url:https://business-one-consulting.com/en/sap-business-one/e-invoice/`,
    },
    {
      id: 'e_invoice_requirements',
      domain: 'business-one-consulting.com',
      headline: $localize`:@@einvoice.recommendations.e_invoice_requirements.headline:E-invoicing in Germany: deadlines and requirements`,
      description: $localize`:@@einvoice.recommendations.e_invoice_requirements.description:What applies to your business in Germany? An overview of requirements, formats and transition periods.`,
      url: $localize`:@@einvoice.recommendations.e_invoice_requirements.url:https://business-one-consulting.com/en/blog/e-invoicing-mandatory-from-2025/`,
    },
    {
      id: 'incoming_invoice_guide',
      domain: 'business-one-consulting.com',
      headline: $localize`:@@einvoice.recommendations.incoming_invoice_guide.headline:Post incoming invoices in SAP Business One`,
      description: $localize`:@@einvoice.recommendations.incoming_invoice_guide.description:Step by step: entering invoices, using base documents, posting and making corrections.`,
      url: $localize`:@@einvoice.recommendations.incoming_invoice_guide.url:https://business-one-consulting.com/en/knowledge/booking-incoming-invoices-in-sap-business-one-a-complete-practical-guide/`,
    },
  ] satisfies Omit<Recommendation, 'href'>[]).map(entry => ({ ...entry, href: this.withUtm(entry.url, entry.id) }));

  toggle(): void {
    this.expanded.update(expanded => !expanded);
  }

  onOpen(entry: Recommendation, event: MouseEvent): void {
    if (event.type === 'auxclick' && event.button !== 1) return;
    if (entry.id === 'sap_demo') {
      try {
        localStorage.setItem(PORTAL_LOCALE_KEY, this.locale);
      } catch {}
    }
    const placement: Placement = this.isSidebar() ? 'right_sidebar' : 'bottom_panel';
    this.analytics.logEvent('viewer_recommendation_click', {
      entry_id: entry.id,
      viewer_language: this.locale,
      placement,
      link_url: entry.url,
    });
  }

  openCookieSettings(): void {
    this.analytics.openCookieConsentDialog();
  }

  private withUtm(url: string, entryId: string): string {
    const target = new URL(url);
    target.searchParams.set('utm_source', 'e_invoice_viewer');
    target.searchParams.set('utm_medium', 'referral');
    target.searchParams.set('utm_campaign', 'viewer_recommendations');
    target.searchParams.set('utm_content', entryId);
    return target.toString();
  }
}
